/**
 * fleetOpsLog — suivi des opérations flotte (ventes) et des dossiers
 * d'assurance (sinistres). Retour apprenant (REX) : « j'ai vendu un véhicule
 * mais je n'ai pas vu où le montant apparaissait » / « j'ai déclaré un vol
 * mais aucun retour sur l'indemnisation ».
 *
 * Chaque opération est un dossier avec un statut qui avance jour après jour
 * (advanceFleetOps, appelé après le set() final de nextDay) :
 *   vente   : listed → signed (J+1) → credited (J+2, virement)
 *   sinistre: declared → in_review (J+1) → paid (J+3 réparation, J+5 vol)
 */
export const FLEET_OPS_MAX = 40;
export const CLAIM_REVIEW_DAYS = 1;
export const CLAIM_PAYOUT_DAYS = 3;
export const THEFT_PAYOUT_DAYS = 5;

export const FLEET_OP_STATUS_LABELS = {
  listed: 'Mise en vente',
  signed: 'Offre signée',
  credited: 'Virement reçu',
  declared: 'Déclaration reçue',
  in_review: 'Dossier en cours',
  paid: 'Indemnisation versée',
  refused: 'Indemnisation refusée',
};

const vehicleLabel = (v) => `${v.brand || ''} ${v.model || ''}`.trim() || 'Véhicule';

export function createSaleOp({ id, vehicle, price, day, completionDay }) {
  return {
    id,
    kind: 'sale',
    vehicleId: vehicle.id,
    vehicleLabel: vehicleLabel(vehicle),
    amount: price,
    status: 'listed',
    createdDay: day,
    reviewDay: day + 1,
    payoutDay: completionDay,
    creditedDay: null,
  };
}

export function createClaimOp({ id, vehicle, claimType, day, repairCost = 0, reimbursement, franchise = 0, coverageRate, tier, refused = false, payoutDays }) {
  const delay = payoutDays ?? (claimType === 'theft' ? THEFT_PAYOUT_DAYS : CLAIM_PAYOUT_DAYS);
  return {
    id,
    kind: 'claim',
    claimType,
    vehicleId: vehicle.id,
    vehicleLabel: vehicleLabel(vehicle),
    repairCost,
    amount: refused ? 0 : reimbursement,
    franchise,
    coverageRate,
    tier,
    status: refused ? 'refused' : 'declared',
    createdDay: day,
    reviewDay: day + CLAIM_REVIEW_DAYS,
    payoutDay: refused ? null : day + delay,
    creditedDay: refused ? day : null,
  };
}

export function appendFleetOp(log, op) {
  return [op, ...(log || [])].slice(0, FLEET_OPS_MAX);
}

/**
 * Avance les statuts au jour `day`. Renvoie le nouveau log, les indemnités
 * à créditer aujourd'hui et les ventes dont le virement vient d'arriver.
 */
export function advanceFleetOps(log, day) {
  const payouts = [];
  const saleCredited = [];
  const next = (log || []).map((op) => {
    if (op.kind === 'sale') {
      if (op.status !== 'credited' && day >= op.payoutDay) {
        const done = { ...op, status: 'credited', creditedDay: day };
        saleCredited.push(done);
        return done;
      }
      if (op.status === 'listed' && day >= op.reviewDay) return { ...op, status: 'signed' };
      return op;
    }
    if (op.kind === 'claim') {
      if ((op.status === 'declared' || op.status === 'in_review') && op.payoutDay != null && day >= op.payoutDay) {
        const done = { ...op, status: 'paid', creditedDay: day };
        payouts.push(done);
        return done;
      }
      if (op.status === 'declared' && day >= op.reviewDay) return { ...op, status: 'in_review' };
      return op;
    }
    return op;
  });
  return { log: next, payouts, saleCredited };
}

export const isFleetOpOpen = (op) => !['credited', 'paid', 'refused'].includes(op.status);

/** Étapes affichables (UI) : [{ label, day, done, current }]. */
export function fleetOpSteps(op) {
  const chain = op.kind === 'sale'
    ? [['listed', op.createdDay], ['signed', op.reviewDay], ['credited', op.payoutDay]]
    : op.status === 'refused'
      ? [['declared', op.createdDay], ['refused', op.creditedDay]]
      : [['declared', op.createdDay], ['in_review', op.reviewDay], ['paid', op.payoutDay]];
  const idx = chain.findIndex(([key]) => key === op.status);
  return chain.map(([key, day], i) => ({
    key,
    label: FLEET_OP_STATUS_LABELS[key],
    day,
    done: i <= idx,
    current: i === idx,
  }));
}
